import type {
  CampaignIn,
  CampaignOut,
  MembershipRuleIn,
  MembershipRuleOut,
  SegmentIn,
  SegmentOut,
  SeriesIn,
  SeriesOut,
  StepSpecIn,
  TemplateIn,
  TemplateOut,
} from "../../components/Promotions/DynamicBonuses/types";

export const LABELLED_PRIORITY_VALUES = {
  low: 1,
  medium: 5,
  high: 8,
} as const;

export type LabelledPriority = keyof typeof LABELLED_PRIORITY_VALUES;

export const toWritableCampaignPriority = (
  priority: CampaignOut["priority"],
): number | undefined => {
  if (typeof priority === "number") return priority;
  if (typeof priority !== "string") return undefined;

  const normalized = priority.trim().toLowerCase();
  return normalized in LABELLED_PRIORITY_VALUES
    ? LABELLED_PRIORITY_VALUES[normalized as LabelledPriority]
    : undefined;
};

/**
 * GET echoes `trigger.source` / `trigger.event_type` for display, but write
 * schemas are `extra="forbid"` and derive those server-side — leave them on
 * the wire and create/update/duplicate 422.
 */
const toWritableSteps = (steps: StepSpecIn[]): StepSpecIn[] =>
  steps.map((step) => {
    const { source: _source, event_type: _eventType, ...trigger } = step.trigger;
    return { ...step, trigger };
  });

/** GET echoes `trigger_key`; write schemas are extra="forbid" and take `event_key`. */
const toWritableMembershipRules = (
  rules: Array<MembershipRuleIn | MembershipRuleOut> | undefined,
): MembershipRuleIn[] =>
  (rules ?? []).flatMap((rule) => {
    if (!rule.event_key) return [];
    return [
      {
        ...(rule.rule_id ? { rule_id: rule.rule_id } : {}),
        event_key: rule.event_key,
        field_checks: rule.field_checks,
        ...(rule.ttl_days != null ? { ttl_days: rule.ttl_days } : {}),
      },
    ];
  });

// Strips server-only fields before a PUT — the backend schema is extra="forbid", so any stray key 422s.
export function toCampaignIn(
  campaign: CampaignOut,
  overrides: Partial<CampaignIn> = {},
): CampaignIn {
  return {
    name: campaign.name,
    starts_at: campaign.starts_at,
    ends_at: campaign.ends_at ?? null,
    priority: toWritableCampaignPriority(campaign.priority),
    // Steps forward mostly whole so `steps[].banner` round-trips, but strip
    // read-only trigger echoes first (see `toWritableSteps`).
    steps: toWritableSteps(campaign.steps),
    caps: campaign.caps,
    eligibility_conditions: campaign.eligibility_conditions,
    localized_strings: campaign.localized_strings,
    created_from_template_id: campaign.created_from_template_id ?? null,
    ...(campaign.affiliate_referral_code !== undefined
      ? { affiliate_referral_code: campaign.affiliate_referral_code }
      : {}),
    // BONENG-724 — preserve referral category + config on PUT. Category is
    // immutable upstream, so it must round-trip unchanged.
    ...(campaign.category !== undefined ? { category: campaign.category } : {}),
    ...(campaign.referral_config !== undefined
      ? { referral_config: campaign.referral_config }
      : {}),
    ...overrides,
  };
}

// BONENG-842 — series write body must match upstream SeriesIn (extra="forbid").
// Unlike referral campaigns on the campaigns endpoint, the series endpoint
// never accepts `category` / `referral_config` / `localized_strings`.
export function toSeriesIn(
  series: SeriesOut,
  overrides: Partial<SeriesIn> = {},
): SeriesIn {
  return {
    name: series.name,
    starts_at: series.starts_at,
    priority: series.priority,
    steps: toWritableSteps(series.steps),
    caps: series.caps,
    eligibility_conditions: series.eligibility_conditions,
    created_from_template_id: series.created_from_template_id ?? null,
    ...overrides,
  };
}

export function toSegmentIn(
  segment: SegmentOut,
  overrides: Partial<SegmentIn> = {},
): SegmentIn {
  const { membership_rules: overrideRules, ...restOverrides } = overrides;
  return {
    name: segment.name,
    description: segment.description,
    membership_ttl_days: segment.membership_ttl_days ?? null,
    membership_rules: toWritableMembershipRules(
      overrideRules ?? segment.membership_rules,
    ),
    ...restOverrides,
  };
}

export function toTemplateIn(
  template: TemplateOut,
  overrides: Partial<TemplateIn> = {},
): TemplateIn {
  return {
    name: template.name,
    description: template.description,
    default_steps: template.default_steps,
    default_localized_strings: template.default_localized_strings,
    default_caps: template.default_caps,
    default_eligibility: template.default_eligibility,
    ...overrides,
  };
}
